import { redirect } from "@tanstack/react-router";
import { useAdminStore } from "../stores/adminStore";

/**
 * Route guard configuration for a module
 */
interface RouteGuardConfig {
  /** The permission required to access the route */
  requiredPermission: string;
  /** Human-readable module name for the error page */
  moduleName: string;
  /** The route path being guarded */
  routePath: string;
}

/**
 * Wait for Zustand persist to rehydrate from sessionStorage.
 * Returns a promise that resolves when hydration is complete.
 */
async function waitForHydration(): Promise<void> {
  // Check if already hydrated
  if (useAdminStore.persist.hasHydrated()) {
    return;
  }

  // Wait for hydration to complete
  return new Promise((resolve) => {
    const unsubscribe = useAdminStore.persist.onFinishHydration(() => {
      unsubscribe();
      resolve();
    });
  });
}

/**
 * Creates a beforeLoad guard that checks permissions and redirects to /unauthorized if denied.
 *
 * This function is designed to be used in TanStack Router's beforeLoad option.
 * It accesses the Zustand store directly (not via hook) since beforeLoad runs outside React.
 * It waits for Zustand persist hydration before checking permissions.
 *
 * @example
 * ```tsx
 * export const Route = createFileRoute("/mosquito")({
 *   beforeLoad: createPermissionGuard({
 *     requiredPermission: "Mosquito.Read",
 *     moduleName: "Mosquito Pools",
 *     routePath: "/mosquito",
 *   }),
 * });
 * ```
 */
export function createPermissionGuard(config: RouteGuardConfig) {
  return async () => {
    const { requiredPermission, moduleName, routePath } = config;

    // Check if user is authenticated using MSAL instance
    // Import dynamically to avoid circular dependencies
    const { msalInstance } = await import("../main");
    const accounts = msalInstance.getAllAccounts();

    // If not authenticated, redirect to home (not unauthorized)
    if (accounts.length === 0) {
      throw redirect({ to: "/" });
    }

    // Wait for Zustand to hydrate from sessionStorage
    await waitForHydration();

    // Get permissions directly from the Zustand store (works outside React)
    const state = useAdminStore.getState();

    // Find the current user's permissions
    // Check all userDetail entries since beforeLoad doesn't have React context
    let hasPermission = false;

    for (const userId of Object.keys(state.userDetail)) {
      const userDetail = state.userDetail[userId];
      if (
        userDetail?.permissions?.some(
          (p) => p.permissionName === requiredPermission
        )
      ) {
        hasPermission = true;
        break;
      }
    }

    if (!hasPermission) {
      throw redirect({
        to: "/unauthorized",
        search: {
          attemptedRoute: routePath,
          requiredPermission,
          moduleName,
        },
      });
    }
  };
}

/**
 * Pre-configured guards for each module
 */
export const routeGuards = {
  mosquito: createPermissionGuard({
    requiredPermission: "Mosquito.Read",
    moduleName: "Mosquito Pools",
    routePath: "/mosquito",
  }),
  sentinel: createPermissionGuard({
    requiredPermission: "Sentinel.Read",
    moduleName: "Sentinel Flocks",
    routePath: "/sentinel",
  }),
  veterinary: createPermissionGuard({
    requiredPermission: "Veterinary.Read",
    moduleName: "Veterinary Cases",
    routePath: "/veterinary",
  }),
  admin: createPermissionGuard({
    requiredPermission: "Users.Read",
    moduleName: "User Administration",
    routePath: "/admin",
  }),
};
